/**
 * AppErrorBoundary — catch any render-time throw so users never see a blank
 * white page.
 *
 * Without this, an unhandled error anywhere in the tree causes React 18 to
 * unmount everything. The user sees a completely blank page with no way to
 * recover except a manual hard refresh. This surfaced during Session 6.5
 * when stale localStorage auth state caused Home sub-components to
 * dereference a null `user`.
 *
 * This is a safety net, NOT an excuse for buggy components. It shows a
 * minimal friendly panel with a "Reload" action and captures the error to
 * the console so it's still visible in browser DevTools + Sentry (if wired
 * up later).
 */
import React from "react";

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console so devs still see the real stack.
    // Structured so any future error-reporting integration can grep it.
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary] uncaught render error:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    // Clear the persist store so a corrupted user state can't relaunch us
    // straight back into the same crash on reload. Preserve nothing.
    try {
      localStorage.removeItem("fitfrik-auth");
      localStorage.removeItem("ff_token");
      localStorage.removeItem("ff_refresh");
    } catch {
      /* ignore quota / privacy-mode errors */
    }
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          background: "#F7F7F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "Barlow, system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 460,
            width: "100%",
            background: "#FFFFFF",
            border: "1px solid rgba(12,12,11,0.08)",
            borderRadius: 16,
            padding: 24,
            textAlign: "left",
          }}
        >
          <h1
            style={{
              fontFamily: "Barlow Condensed, sans-serif",
              fontSize: 28,
              fontWeight: 800,
              marginBottom: 8,
              color: "#0C0C0B",
            }}
          >
            Something went wrong.
          </h1>
          <p style={{ fontSize: 14, color: "#4A4845", lineHeight: 1.5, marginBottom: 20 }}>
            The app hit an error while rendering. Your data is safe. Tap
            below to reset the session and reload.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="app-error-reload"
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: 100,
              border: "none",
              background: "#0C0C0B",
              color: "#FFFFFF",
              fontFamily: "Barlow, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Reload FitFrik
          </button>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre
              style={{
                marginTop: 16,
                padding: 12,
                background: "#F0EFEC",
                borderRadius: 8,
                fontSize: 11,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "#4A4845",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
